import { useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { products, faqQuestions } from "@/lib/products";
import Layout from "@/components/Layout";
import { scrollToTop } from "@/utils/scrollUtils";
import ProductHeader from "@/components/product/ProductHeader";
import ProductGallery from "@/components/product/ProductGallery";
import AvailableForSection from "@/components/product/AvailableForSection";
import RelatedProducts from "@/components/product/RelatedProducts";
import { useToast } from "@/hooks/use-toast";

export default function ProductDetail() {
  const { id } = useParams();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    requirement: "",
    email: "",
    country: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    scrollToTop();
  }, [id]);

  const product = products.find((p) => p.id === id);

  if (!product) {
    return <div>Product not found</div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    const webFormData = {
      access_key: "59e9a68c-6f3f-4cd0-996e-0044fb9e0f56",
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      country: formData.country,
      product: product.name,
      message: formData.requirement,
      subject: `Trade Inquiry for ${product.name}`,
    };

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(webFormData),
      });

      const result = await response.json();

      if (response.status === 200) {
        toast({
          title: "Inquiry Submitted Successfully!",
          description: "We will get back to you soon.",
        });
        setFormData({
          name: "",
          phone: "",
          requirement: "",
          email: "",
          country: "",
        });
      } else {
        throw new Error(result.message || "Something went wrong!");
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit the form. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb className="mb-8">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/products">Products</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>{product.name}</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div>
            <ProductGallery product={product} />
          </div>
          <div className="space-y-8">
            <ProductHeader product={product} />
            <AvailableForSection product={product} />
          </div>
        </div>

        <RelatedProducts currentProductId={product.id} products={products} />

        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Send International Trade Inquiry</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              placeholder="Company Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
            />
            <Input
              placeholder="Phone Number (with country code)"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
            />
            <Input
              placeholder="Business Email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
            />
            <Input
              placeholder="Country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              required
            />
            <Textarea
              placeholder="Specify your requirements, target market, and preferred shipping terms"
              name="requirement"
              value={formData.requirement}
              onChange={handleInputChange}
              className="md:col-span-2"
              required
            />
            <Button 
              type="submit" 
              className="md:col-span-2 bg-[#ea384c] hover:bg-[#ea384c]/90"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Trade Inquiry"}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
